import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IoMdArrowBack } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchMyReports,
    fetchMyViolations,
    fetchMyAppeals,
    fetchViolationDetail,
    submitAppeal,
    resetAppealState,
    resetViolationDetail,
} from "../../redux/ApiSlice/ReportCenterSlice";

const STATUS_COLORS = {
    "Pending Review": "text-[#1DA1F2]",
    "Report Unsuccessful": "text-[#F44336]",
    "Report Successful": "text-[#00B894]",
    Approved: "text-[#00B894]",
    Rejected: "text-[#F44336]",
};

const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const Thumbnail = ({ src, className = "h-24 w-16 rounded-xl shrink-0" }) =>
    src ? (
        <img src={src} alt="" className={`${className} object-cover bg-[#D9D9D9]`} />
    ) : (
        <div className={`${className} bg-[#D9D9D9]`} />
    );

const tabs = [
    { key: "reports", label: "Your Reports" },
    { key: "violations", label: "Your Violations" },
    { key: "appeals", label: "Your Appeals" },
];

const ReportCenter = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState(location.state?.initialTab || "reports");
    const [selectedViolation, setSelectedViolation] = useState(null);
    const [showActionSheet, setShowActionSheet] = useState(false);
    const [appealText, setAppealText] = useState("");
    const [showAppealView, setShowAppealView] = useState(false);
    const [portalRoot, setPortalRoot] = useState(null);

    useEffect(() => {
        setPortalRoot(document.getElementById("mobile-layout-root"));
    }, []);

    const { myReports, myViolations, myAppeals, violationDetail, appeal } = useSelector(
        (state) => state.ReportCenter
    );

    useEffect(() => {
        dispatch(fetchMyReports());
        dispatch(fetchMyViolations());
        dispatch(fetchMyAppeals());
    }, [dispatch]);

    useEffect(() => {
        if (appeal.success) {
            setShowAppealView(false);
            setSelectedViolation(null);
            setAppealText("");
            dispatch(fetchMyAppeals());
            dispatch(fetchMyViolations());
            dispatch(resetAppealState());
            dispatch(resetViolationDetail());
        }
    }, [appeal.success, dispatch]);

    const handleOpenActionSheet = (violation) => {
        setSelectedViolation(violation);
        setShowActionSheet(true);
    };

    const handleCloseActionSheet = () => {
        setShowActionSheet(false);
        setSelectedViolation(null);
    };

    const handleOpenAppealView = () => {
        if (!selectedViolation) return;
        setShowActionSheet(false);
        setShowAppealView(true);
        dispatch(fetchViolationDetail(selectedViolation.post_id));
    };

    const handleBack = () => {
        if (showAppealView) {
            setShowAppealView(false);
            setSelectedViolation(null);
            setAppealText("");
            dispatch(resetAppealState());
            dispatch(resetViolationDetail());
            return;
        }
        navigate("/account/settings");
    };

    const handleSubmitAppeal = () => {
        if (!appealText.trim() || appeal.loading || appeal.success || !selectedViolation) return;
        dispatch(submitAppeal({ postId: selectedViolation.post_id, appealText }));
    };

    const renderReports = () => {
        if (myReports.loading)
            return <p className="text-center text-[12px] text-[#666] dark:text-gray-400 mt-6">Loading...</p>;
        if (myReports.error)
            return <p className="text-center text-[12px] text-[#F44336] mt-6">Failed to load reports.</p>;
        if (!myReports.data.length)
            return <p className="text-center text-[12px] text-[#666] dark:text-gray-400 mt-6">No reports yet.</p>;

        return (
            <div className="px-4 pb-6">
                {myReports.data.map((item) => (
                    <div key={item.id} className="flex gap-3 border-b border-[#D9D9D9] dark:border-gray-700 py-4">
                        <Thumbnail src={item.post_thumbnail} />
                        <div>
                            <p className="text-[12px] font-bold text-[#2F2F2F] dark:text-white">
                                {item.post_owner_name}
                            </p>
                            <p className="text-[12px] font-semibold text-[#666666] dark:text-gray-400">
                                Reported on {formatDate(item.reported_on)}
                            </p>
                            <p className="text-[12px] font-semibold text-[#666666] dark:text-gray-400">
                                Status:{" "}
                                <span className={STATUS_COLORS[item.status] ?? "text-[#666] dark:text-gray-400"}>
                                    {item.status}
                                </span>
                            </p>
                            <p className="text-[12px] text-[#666666] dark:text-gray-400 font-semibold leading-[14px] mt-1">
                                {item.status_message}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderViolations = () => {
        if (myViolations.loading)
            return <p className="text-center text-[12px] text-[#666] dark:text-gray-400 mt-6">Loading...</p>;
        if (myViolations.error)
            return <p className="text-center text-[12px] text-[#F44336] mt-6">Failed to load violations.</p>;
        if (!myViolations.data.length)
            return <p className="text-center text-[12px] text-[#666] dark:text-gray-400 mt-6">No violations.</p>;

        return (
            <div className="px-4 pb-6 mt-1">
                {myViolations.data.map((item, index) => (
                    <div key={item.post_id}>
                        <div
                            className={`py-4 px-2 rounded-xl transition-colors ${selectedViolation?.post_id === item.post_id
                                ? "bg-[#BDE6FF]"
                                : "bg-transparent"
                                }`}
                        >
                            <div className="flex items-center justify-between">
                                <Thumbnail src={item.thumbnail} />
                                <HiOutlineDotsHorizontal
                                    onClick={() => handleOpenActionSheet(item)}
                                    className="text-[20px] text-[#444444] dark:text-white cursor-pointer"
                                />
                            </div>
                            <div className={`mt-3 text-[12px] font-semibold leading-5 ${selectedViolation?.post_id === item.post_id
                                ? "text-[#444444]"
                                : "text-[#444444] dark:text-gray-300"
                                }`}>
                                <p className="line-clamp-2">{item.caption}</p>
                                <p>Posted: {formatDate(item.posted_at)}</p>
                                <p>Reported from: {item.reported_from}</p>
                                <p>Reason: {item.reason}</p>
                                <p>
                                    Status:{" "}
                                    <span className={STATUS_COLORS[item.status] ?? (selectedViolation?.post_id === item.post_id ? "text-[#444444]" : "text-[#444444] dark:text-gray-300")}>
                                        {item.status}
                                    </span>
                                </p>
                            </div>
                        </div>
                        {index !== myViolations.data.length - 1 &&
                            selectedViolation?.post_id !== item.post_id &&
                            !showActionSheet && <div className="border-b border-[#D9D9D9] dark:border-gray-700" />}
                    </div>
                ))}
            </div>
        );
    };

    const renderAppeals = () => {
        if (myAppeals.loading)
            return <p className="text-center text-[12px] text-[#666] dark:text-gray-400 mt-6">Loading...</p>;
        if (myAppeals.error)
            return <p className="text-center text-[12px] text-[#F44336] mt-6">Failed to load appeals.</p>;
        if (!myAppeals.data.length)
            return <p className="text-center text-[12px] text-[#666] dark:text-gray-400 mt-6">No appeals submitted.</p>;

        return (
            <div className="px-4 pb-6">
                {myAppeals.data.map((item) => (
                    <div key={item.id} className="border-b border-[#D9D9D9] dark:border-gray-700 py-4">
                        <Thumbnail src={item.thumbnail} />
                        <div className="mt-3 text-[12px] font-semibold text-[#444444] dark:text-gray-300 leading-5">
                            <p className="line-clamp-2">{item.caption}</p>
                            <p>Originally posted: {formatDate(item.originally_posted)}</p>
                            <p>Reported from: {item.reported_from}</p>
                            <p>Violation: {item.violation}</p>
                            <p>
                                Appeal Status:{" "}
                                <span className={STATUS_COLORS[item.appeal_status] ?? "text-[#666]"}>
                                    {item.appeal_status}
                                </span>
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-[#1E1D21] min-h-screen">
            <div className="sticky top-0 z-50 bg-[#F4F4F4] dark:bg-[#1E1D21]">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={handleBack}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">
                        {showAppealView ? "Make an appeal" : "Report Center"}
                    </p>
                </div>
            </div>

            {!showAppealView && (
                <div className="px-4 pt-3">
                    <div className="flex border-b border-[#BEBEBE] dark:border-gray-700/50">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                className="relative flex-1 py-2 text-[12px] text-center text-[#666666] dark:text-gray-400"
                            >
                                <span
                                    className={
                                        activeTab === tab.key
                                            ? "font-semibold text-[#2F2F2F] dark:text-white"
                                            : "font-normal"
                                    }
                                >
                                    {tab.label}
                                </span>
                                {activeTab === tab.key && (
                                    <motion.div
                                        layoutId="report-tab-indicator"
                                        className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#2F2F2F] dark:bg-white rounded-full"
                                        transition={{ type: "spring", stiffness: 500, damping: 40 }}
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <AnimatePresence mode="wait">
                {showAppealView ? (
                    <motion.div
                        key="appeal-view"
                        initial={{ opacity: 0, x: 25 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -25 }}
                        transition={{ duration: 0.2 }}
                        className="px-4 pt-3 pb-6"
                    >
                        {selectedViolation && (
                            <div className="border border-[#D9D9D9] dark:border-gray-800/50 rounded-xl p-3 mb-4 bg-[#F9F9F9] dark:bg-[#333333]">
                                <div className="flex items-start gap-3">
                                    <Thumbnail
                                        src={selectedViolation.thumbnail}
                                        className="h-16 w-12 rounded-lg shrink-0"
                                    />
                                    <div className="text-[11px] font-semibold text-[#444444] dark:text-gray-300 leading-5">
                                        <p className="line-clamp-2">{selectedViolation.caption}</p>
                                        <p>Posted: {formatDate(selectedViolation.posted_at)}</p>
                                        <p>Reported from: {selectedViolation.reported_from}</p>
                                        <p>Reason: {selectedViolation.reason}</p>
                                        <p>
                                            Status:{" "}
                                            <span className={STATUS_COLORS[selectedViolation.status] ?? "text-[#444444] dark:text-gray-300"}>
                                                {selectedViolation.status}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {violationDetail.loading && (
                                    <p className="text-[10px] text-[#888] dark:text-gray-400 mt-3">Loading report details...</p>
                                )}

                                {!violationDetail.loading && violationDetail.error && (
                                    <p className="text-[10px] text-[#F44336] mt-3">Failed to load report details.</p>
                                )}

                                {!violationDetail.loading && !violationDetail.error && violationDetail.data?.results?.length > 0 && (
                                    <div className="mt-3 border-t border-[#E5E5E5] pt-3 space-y-2">
                                        {violationDetail.data.results.map((submission) => (
                                            <div key={submission.id} className="text-[10px] text-[#666] dark:text-gray-400 leading-4">
                                                <p><span className="font-semibold text-[#444] dark:text-gray-300">Reason:</span> {submission.reason}</p>
                                                {submission.sub_reason && submission.sub_reason !== "N/A" && (
                                                    <p><span className="font-semibold text-[#444] dark:text-gray-300">Sub-reason:</span> {submission.sub_reason}</p>
                                                )}
                                                {submission.user_comment && submission.user_comment !== "N/A" && (
                                                    <p><span className="font-semibold text-[#444] dark:text-gray-300">Comment:</span> {submission.user_comment}</p>
                                                )}
                                                <p className="text-[#999] dark:text-gray-500">{formatDate(submission.reported_on)}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <p className="text-[14px] font-semibold text-[#2F2F2F] dark:text-white mb-4">
                            Appeal this decision
                        </p>

                        {appeal.error && (
                            <p className="text-[12px] text-[#F44336] mb-3">
                                {appeal.error?.detail ?? appeal.error?.message ?? "Something went wrong."}
                            </p>
                        )}

                        <div className="border border-[#BFBFBF] dark:border-gray-800/50 rounded-xl p-3 bg-transparent dark:bg-[#333333]">
                            <textarea
                                maxLength={250}
                                value={appealText}
                                disabled={appeal.loading || appeal.success}
                                onChange={(e) => setAppealText(e.target.value)}
                                placeholder="Why should this post be allowed on Chaa Chingg?"
                                className={`w-full h-20 resize-none outline-none bg-transparent text-[11px] placeholder:text-[#9CA3AF] ${appeal.loading || appeal.success
                                    ? "text-[#9CA3AF] cursor-not-allowed"
                                    : "text-[#444444] dark:text-white"
                                    }`}
                            />
                        </div>
                        <p className="text-right text-[13px] text-[#8B8B8B] dark:text-gray-400 mt-1">{appealText.length}/250</p>
                        <p className="text-center text-[11px] text-[#8B8B8B] dark:text-gray-400 mt-3">
                            Once submitted, you cannot edit or cancel your appeal.
                        </p>

                        <div className="mt-4 grid grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowAppealView(false);
                                    setSelectedViolation(null);
                                    setAppealText("");
                                    dispatch(resetAppealState());
                                    dispatch(resetViolationDetail());
                                }}
                                className="h-10 rounded-xl bg-[#E0E0E0] dark:bg-[#444444] text-[14px] font-semibold text-[#2F2F2F] dark:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmitAppeal}
                                disabled={!appealText.trim() || appeal.loading || appeal.success || !selectedViolation}
                                className={`h-10 rounded-xl text-[14px] font-semibold ${!appealText.trim() || appeal.loading || appeal.success || !selectedViolation
                                    ? "bg-[#E7E7E7] text-[#B8B8B8]"
                                    : "bg-[#BDE6FF] text-[#2F2F2F]"
                                    }`}
                            >
                                {appeal.loading
                                    ? "Submitting..."
                                    : appeal.success
                                        ? "Submitted"
                                        : "Submit appeal"}
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                    >
                        {activeTab === "reports" && renderReports()}
                        {activeTab === "violations" && renderViolations()}
                        {activeTab === "appeals" && renderAppeals()}
                    </motion.div>
                )}
            </AnimatePresence>

            {portalRoot && createPortal(
                <AnimatePresence>
                    {showActionSheet && (
                        <motion.div
                            exit={{ opacity: 0 }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            onClick={handleCloseActionSheet}
                            className="absolute inset-0 z-[200] flex items-end bg-black/40"
                        >
                            <motion.div
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                initial={{ y: "100%" }}
                                onClick={(e) => e.stopPropagation()}
                                transition={{ type: "spring", stiffness: 240, damping: 28 }}
                                className="w-full bg-white dark:bg-[#333333] rounded-t-2xl h-[8rem] overflow-hidden"
                            >
                                <div className="mx-auto mt-3 mb-2 h-1 w-14 rounded-full bg-[#5C5C5C]" />
                                <button
                                    type="button"
                                    onClick={handleOpenAppealView}
                                    className="w-full text-left px-6 py-3 text-[15px] text-black dark:text-white border-b border-[#E6E6E6] dark:border-gray-700"
                                >
                                    Make an appeal
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleCloseActionSheet();
                                        navigate("/support");
                                    }}
                                    className="w-full text-left px-6 py-3 text-[15px] text-black dark:text-white"
                                >
                                    View Community Guidelines
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                portalRoot
            )}
        </div>
    );
};

export default ReportCenter;
